import { useEffect, useState } from "react";
import Input from "ui/forms/Input";
import InputGroup from "ui/forms/InputGroup";

const NUMBER_FORMAT_LOCALE = "en-US";

interface InputGroupNumberProps {
  onChange: (value: number | null) => void;
  inputValue?: number;
  labelText: string;
  placeholder?: string;
  disabled?: boolean;
  validate?: () => string | undefined;
  tooltip?: string;
  className?: string;
}

function InputGroupNumber({
  placeholder,
  labelText,
  onChange,
  inputValue,
  disabled,
  validate,
  tooltip,
  className,
}: InputGroupNumberProps) {
  const [inputState, setInputState] = useState<"error" | "success" | null>(null);
  const [errorText, setErrorText] = useState("");
  const [displayedValue, setDisplayedValue] = useState<string | null>(null);

  useEffect(() => {
    setDisplayedValue(null);
  }, []);

  const handleNumberChange = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    const parsedFloat = parseFloat(value.split(",").join(""));

    if (parsedFloat && parsedFloat !== 0 && parsedFloat % 1 !== 0) {
      setErrorText(`Must be a whole number.`);
      setInputState("error");
    } else {
      setInputState(null);
      setErrorText("");
    }

    if (value === "-") {
      onChange(0);
      setDisplayedValue("-");
    } else if (value.endsWith(".") || value.startsWith(".")) {
      if (value.split(".").length > 2) {
        return;
      }
      onChange(parseInt(value.split(".").join("")));
      setDisplayedValue(value);
    } else if (!isNaN(parsedFloat)) {
      onChange(parsedFloat);
      setDisplayedValue(parsedFloat.toLocaleString(NUMBER_FORMAT_LOCALE));
    } else if (value === "") {
      onChange(null);
      setDisplayedValue(value);
    }
    return;
  };

  return (
    <InputGroup labelText={labelText} tooltip={tooltip} className={className} errorText={errorText}>
      <Input
        onChange={(e) => handleNumberChange(e)}
        value={displayedValue ?? inputValue?.toLocaleString(NUMBER_FORMAT_LOCALE)}
        type="text"
        placeholder={placeholder ?? `Enter ${labelText}`}
        disabled={disabled}
        inputState={inputState ?? undefined}
        validate={validate}
      />
    </InputGroup>
  );
}

export default InputGroupNumber;
